/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Gets field selectors for finding form fields in the DOM.
 * 
 * @param {string} fieldName - Name of the field to find
 * @returns {string[]} Array of CSS selectors to try
 */
export function getFieldSelectors(fieldName: string): string[] {
  return [
    `input[name="${fieldName}"]`,
    `select[name="${fieldName}"]`,
    `textarea[name="${fieldName}"]`,
    `[name="${fieldName}"]`,
    `#${fieldName}`,
    `[data-field="${fieldName}"]`,
    `label[for="${fieldName}"]`,
    `#mantine-${fieldName}-input`,
    `#mantine-${fieldName}`,
  ];
}

/**
 * Finds container element for a field by data attributes.
 * 
 * @param {string} fieldName - Name of the field
 * @returns {HTMLElement | null} Container element or null
 */
export function findContainerElement(fieldName: string): HTMLElement | null {
  const containerByDataName = document.querySelector(`[data-field-name="${fieldName}"]`) as HTMLElement | null;
  if (containerByDataName) {
    return containerByDataName;
  }
  
  return document.querySelector(`[data-field="${fieldName}"]`) as HTMLElement | null;
}

/**
 * Finds associated input for a label element.
 * 
 * @param {HTMLElement} labelElement - Label element
 * @returns {HTMLElement | null} Associated input or null
 */
export function findAssociatedInput(labelElement: HTMLElement): HTMLElement | null {
  const forAttr = labelElement.getAttribute('for');
  if (!forAttr) {
    return labelElement;
  }
  
  const associatedInput = document.querySelector(`#${forAttr}`) || 
                         document.querySelector(`[name="${forAttr}"]`);
  if (associatedInput) {
    return associatedInput as HTMLElement;
  }
  
  return labelElement;
}

/**
 * Checks if element is a label with for attribute.
 * 
 * @param {HTMLElement} element - Element to check
 * @returns {boolean} True if element is a label with for attribute
 */
function isLabelWithFor(element: HTMLElement): boolean {
  return element.tagName === 'LABEL' && element.getAttribute('for') !== null;
}

/**
 * Processes found element, handling labels specially.
 * 
 * @param {HTMLElement} found - Found element
 * @returns {HTMLElement | null} Processed element or null
 */
function processFoundElement(found: HTMLElement): HTMLElement | null {
  if (isLabelWithFor(found)) {
    return findAssociatedInput(found);
  }
  return found;
}

/**
 * Searches for field element using selectors.
 * 
 * @param {string[]} selectors - Array of CSS selectors
 * @returns {HTMLElement | null} Found element or null
 */
function searchWithSelectors(selectors: string[]): HTMLElement | null {
  for (const selector of selectors) {
    const found = document.querySelector(selector) as HTMLElement | null;
    if (found) {
      return processFoundElement(found);
    }
  }
  return null;
}

/**
 * Finds field element using multiple selectors.
 * 
 * @param {string} fieldName - Name of the field
 * @param {HTMLElement | null} containerElement - Container element if already found
 * @returns {HTMLElement | null} Field element or null
 */
export function findFieldElement(fieldName: string, containerElement: HTMLElement | null): HTMLElement | null {
  if (containerElement) {
    return containerElement;
  }
  
  const selectors = getFieldSelectors(fieldName);
  return searchWithSelectors(selectors);
}

/**
 * Checks if element is an input element.
 * 
 * @param {HTMLElement} element - Element to check
 * @returns {boolean} True if input element
 */
export function isInputElement(element: HTMLElement): boolean {
  return element.tagName === 'INPUT' || 
         element.tagName === 'SELECT' || 
         element.tagName === 'TEXTAREA';
}

/**
 * Finds closest Mantine container.
 * 
 * @param {HTMLElement} element - Element to search from
 * @returns {HTMLElement | null} Mantine container or null
 */
export function findClosestMantineContainer(element: HTMLElement): HTMLElement | null {
  return element.closest('.mantine-Grid-col') as HTMLElement | null ||
         element.closest('[class*="Grid"]') as HTMLElement | null ||
         element.closest('div[class*="mantine"]') as HTMLElement | null;
}

/**
 * Finds Mantine container for a field element.
 * 
 * @param {HTMLElement | null} fieldElement - Field element
 * @returns {HTMLElement | null} Mantine container or null
 */
export function findMantineContainer(fieldElement: HTMLElement | null): HTMLElement | null {
  if (!fieldElement || !isInputElement(fieldElement)) {
    return null;
  }
  
  return findClosestMantineContainer(fieldElement);
}

/**
 * Gets scrollable container for an element.
 * 
 * @param {HTMLElement} element - Element to find container for
 * @returns {HTMLElement | Document} Scrollable container
 */
export function getScrollableContainer(element: HTMLElement): HTMLElement {
  const formContainer = element.closest('form');
  if (formContainer) {
    return formContainer as HTMLElement;
  }
  
  const dataContainer = element.closest('[data-scroll-container]');
  if (dataContainer) {
    return dataContainer as HTMLElement;
  }
  
  return document.documentElement;
}

/**
 * Gets final container element for scrolling.
 * 
 * @param {HTMLElement | null} fieldElement - Field element
 * @param {HTMLElement | null} containerElement - Initial container
 * @returns {HTMLElement | null} Final container
 */
export function getFinalContainerElement(
  fieldElement: HTMLElement | null,
  containerElement: HTMLElement | null
): HTMLElement | null {
  const mantineContainer = findMantineContainer(fieldElement);
  return mantineContainer || containerElement;
}
